import { useCallback, useRef, useState } from 'react'
import type { useTranslation } from 'react-i18next'
import { env } from '@/env'
import { resolvePublicApiUrl } from '@/shared/lib/api-url'
import {
  clearYandexLinkIntent,
  createYandexLinkCallbackUrl,
} from '@/shared/lib/yandex-link-intent'

export type AuthProvider = {
  id: string
  name: string
  signInPath: string
}

export type LoginStatus = 'loading' | 'ready' | 'signing-in' | 'error'

async function requestSocialSignIn(
  provider: AuthProvider,
  callbackURL: string,
  signal: AbortSignal,
  failureMessage: string,
): Promise<void> {
  const response = await fetch(
    resolvePublicApiUrl(env.VITE_API_URL, provider.signInPath),
    {
      body: JSON.stringify({
        callbackURL,
        disableRedirect: true,
        errorCallbackURL: new URL('/login', window.location.origin).toString(),
        provider: provider.id,
      }),
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      method: 'POST',
      signal,
    },
  )
  const result = (await response.json().catch(() => null)) as {
    url?: unknown
  } | null

  if (!response.ok || typeof result?.url !== 'string') {
    throw new Error(failureMessage)
  }

  window.location.assign(result.url)
}

export function useLoginActions({
  providers,
  returnTo,
  t,
}: {
  providers: Array<AuthProvider>
  returnTo: string
  t: ReturnType<typeof useTranslation>['t']
}) {
  const [status, setStatus] = useState<LoginStatus>('ready')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [activeProviderId, setActiveProviderId] = useState<string | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  const signIn = useCallback(
    async (
      provider: AuthProvider,
      callbackURL = returnTo,
    ): Promise<boolean> => {
      abortControllerRef.current?.abort()
      const controller = new AbortController()
      abortControllerRef.current = controller
      setActiveProviderId(provider.id)
      setStatus('signing-in')
      setErrorMessage(null)

      try {
        await requestSocialSignIn(
          provider,
          callbackURL,
          controller.signal,
          t('auth:signInFailed'),
        )

        return true
      } catch (error: unknown) {
        if (controller.signal.aborted) return false

        setStatus('error')
        setErrorMessage(
          error instanceof Error ? error.message : t('auth:signInFailed'),
        )

        return false
      }
    },
    [returnTo, t],
  )

  const linkAccounts = useCallback(
    async (provider: AuthProvider) => {
      try {
        const callbackURL = createYandexLinkCallbackUrl()
        if (!(await signIn(provider, callbackURL))) clearYandexLinkIntent()
      } catch {
        setErrorMessage(t('auth:linkPreparationFailed'))
      }
    },
    [signIn, t],
  )

  const cancelSignIn = useCallback(() => {
    abortControllerRef.current?.abort()
    abortControllerRef.current = null
    clearYandexLinkIntent()
    setActiveProviderId(null)
    setStatus(providers.length > 0 ? 'ready' : 'error')
  }, [providers])

  return {
    activeProviderId,
    cancelSignIn,
    errorMessage,
    linkAccounts,
    signIn,
    status,
  }
}
